import { AxiosInstance } from 'axios';
import { IBaseService, BaseService } from './BaseServiceClass';
import { APIResponseHandler } from 'utilities/ErrorHelper';
import { IResult } from './Result';
import { ICreateTicketRequest, IUpdateDevopsLinkRequest, IUpdateStateRequest, IUserDiagnostic, IUserDiagnosticsDownloadFileRequestDto, IUserDiagnosticsFileResponse, IUserDiagnosticsRequestDto } from 'types/models/UserDiagnosticsModel';

export interface IUserDiagnosticsService extends IBaseService<IUserDiagnostic> {
    getUserDiagnostic(userDiagnosticId: string): Promise<any>;
    getAllUserDiagnostics(request: IUserDiagnosticsRequestDto): Promise<any>;
    getUserDiagnosticsFile(fileId: string, request?: IUserDiagnosticsDownloadFileRequestDto): Promise<IUserDiagnosticsFileResponse>;
    createTicket(createTicketRequest: ICreateTicketRequest): Promise<IResult<string>>;
    updateUserDiagnosticsState(newState: IUpdateStateRequest): Promise<IResult<boolean>>;
    updateDevopsUrl(updateRequest: IUpdateDevopsLinkRequest): Promise<IResult<boolean>>;
}

export class UserDiagnosticsService extends BaseService<IUserDiagnostic> implements IUserDiagnosticsService {
    constructor(axiosInstance: AxiosInstance) {
        super(axiosInstance, 'api/userdiagnostics');
    }

    getUserDiagnostic = async (userDiagnosticId: string): Promise<any> => {
        const response = await this._axiosInstance.get(`api/userdiagnostics/${userDiagnosticId}`);
        return APIResponseHandler(response);
    }

    getAllUserDiagnostics = async (request: IUserDiagnosticsRequestDto): Promise<any> => {
        const params = new URLSearchParams({
            includeDeleted: request.includeDeleted.toString()
        });
        if (request.updatedAfter) {
            params.append('updatedAfter', request.updatedAfter);
        }

        const response = await this._axiosInstance.get(`api/userdiagnostics?${params.toString()}`);
        return APIResponseHandler(response);
    };

    getUserDiagnosticsFile = async (fileId: string, request?: IUserDiagnosticsDownloadFileRequestDto): Promise<IUserDiagnosticsFileResponse> => {
        let params = new URLSearchParams();
        if (request !== undefined && request !== null) {
            if (request.specificFileType) {
                params.append("specificFileType", request.specificFileType.toString())
            }
            else if (request.specificFileUrl) {
                params.append("specificFileUrl", request.specificFileUrl)
            }
        }

        const response = await this._axiosInstance.get(`api/userdiagnostics/${fileId}/file?${params.toString()}`,
            {
                responseType: 'blob',
            }
        );

        const contentType = response.headers['content-type'] || 'application/octet-stream';
        const typedBlob = new Blob([response.data], { type: contentType });

        let result = {
            blob: typedBlob,
            fileName: this.getFileName(response.headers['content-disposition']),
            status: response.status,
        };

        return result;
    }

    private getFileName(disposition: string): string {
        let fileName = 'downloaded-file';
        if (disposition && disposition.includes('filename=')) {
            const match = disposition.match(/filename="?(.+?)"?$/);
            if (match?.[1]) {
                fileName = match[1];
            }
        }
        return fileName;
    }

    createTicket = async (createTicketRequest: ICreateTicketRequest): Promise<IResult<string>> => {
        const response = await this._axiosInstance.post<string>(`api/userdiagnostics/createticket`, createTicketRequest);
        return APIResponseHandler(response);
    }

    updateUserDiagnosticsState = async (request: IUpdateStateRequest): Promise<IResult<boolean>> => {
        const response = await this._axiosInstance.patch<boolean>(`api/userdiagnostics/updateState`, request);
        return APIResponseHandler(response);
    }

    updateDevopsUrl = async (request: IUpdateDevopsLinkRequest): Promise<IResult<boolean>> => {
        const response = await this._axiosInstance.patch<boolean>(`api/userdiagnostics/devopsUrl`, request);
        return APIResponseHandler(response);
    }
}